// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
'use client';
// "Search everything": World's search box, in every Flameco app (pill in the
// top bar, or Ctrl+K / Cmd+K). Finds Flameco apps, every app's pages, work
// orders, sales orders, POs, RMAs and items by number (GET
// {apiBase}/flameco/record-search), and people, knowledge and upcoming events
// from Flameco World (GET {apiBase}/flameco-world/search). When search has no
// answer, or the person asks a question, it hands the words to Doug (Ask
// Alpha) for people who have Doug. Results open in the same tab; the person is
// already signed in there. NetSuite records open in a new tab.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appUrl, FLAMECO_PAGES, FlamecoApp, FlamecoAppKey } from './apps';
import { askDoug, dougAvailable, dougConnection, hideDoug, minimizeDoug } from './Doug';
import { AiAnswerCard, useDougAnswer } from './DougAnswer';
import { useVoiceSearch, voiceSupported } from './Voice';
import {
  APP_ICONS,
  IconCalendar,
  IconFile,
  IconArrowRight,
  IconHistory,
  IconLayout,
  IconLink,
  IconMic,
  IconPackage,
  IconQuestion,
  IconSearch,
  IconSparkles,
  IconUser,
} from './icons';

export interface GlobalSearchConfig {
  /** The customer-portal backend that serves /flameco-world/search and /flameco/record-search. */
  apiBase: string;
  /** The app's current Flameco access token (flameco-auth getToken). */
  getToken: () => string | null;
}

type Kind = 'doug' | 'recent' | 'record' | 'open' | 'app' | 'page' | 'person' | 'doc' | 'question' | 'event' | 'loading';

interface Row {
  kind: Kind;
  name: string;
  meta: string;
  href: string;
  /** Opens in this app (router), not a full page load. */
  local?: boolean;
  /** Opens in a new tab (NetSuite). */
  external?: boolean;
  /** Short type tag for records (WO, SO, PO, ...). */
  pill?: string;
  /** Record status ("In Process") and how it reads. */
  status?: string | null;
  tone?: 'open' | 'done' | 'hold';
  /** Extra words that find it but aren't shown. */
  keywords?: string;
  /** Icon plate colour (app hue); null = neutral. */
  hue?: number | null;
  /** App key, for the app icon. */
  appKey?: FlamecoAppKey;
  /** The NetSuite page, for records that open in MFG. */
  netsuiteUrl?: string;
  /** NetSuite record kind + internal id, for the preview card. */
  rec?: { kind: string; id: string };
  /** "Open it in" rows: the key that opens it from anywhere in the list. */
  keys?: string;
}

/** Filter tabs under the input; Tab / Shift+Tab move between them. */
type Scope = 'all' | 'records' | 'pages' | 'people' | 'knowledge' | 'doug';
const SCOPES: { key: Scope; label: string; kinds: Kind[] }[] = [
  { key: 'all', label: 'All', kinds: [] },
  { key: 'records', label: 'Records', kinds: ['record'] },
  { key: 'pages', label: 'Apps', kinds: ['open', 'app', 'page'] },
  { key: 'people', label: 'People', kinds: ['person'] },
  { key: 'knowledge', label: 'Knowledge', kinds: ['doc', 'question', 'event'] },
  { key: 'doug', label: 'Flameco AI', kinds: ['doug'] },
];

/** Result groups. Typing a number puts orders first; words put pages first. */
const GROUPS: { label: string; kinds: Kind[] }[] = [
  { label: 'Orders and items', kinds: ['record', 'loading'] },
  { label: 'Apps and pages', kinds: ['app', 'page'] },
  { label: 'People', kinds: ['person'] },
  { label: 'Knowledge', kinds: ['doc', 'question'] },
  { label: 'Events', kinds: ['event'] },
];

const RECORD_PILL: Record<string, string> = {
  workOrder: 'WO',
  salesOrder: 'SO',
  purchaseOrder: 'PO',
  rma: 'RMA',
  fulfillment: 'IF',
  quote: 'Quote',
  item: 'Item',
};

/** What Doug can be asked about a record, by its kind. */
function dougQuestions(r: Row): string[] {
  const n = r.name;
  const kind = r.rec?.kind;
  const open = r.tone === 'open';
  switch (kind) {
    case 'salesOrder':
      return [
        `Where is ${n} on the shop floor?`,
        open ? `What is blocking fulfillment of ${n}?` : `What shipped on ${n} and when?`,
        `Draft a ship-date update for the customer on ${n}`,
      ];
    case 'workOrder':
      return [`Where is ${n} on the shop floor?`, `What components are short for ${n}?`, `When will ${n} be built?`];
    case 'purchaseOrder':
      return [`When will PO ${n} arrive?`, `What is still open on PO ${n}?`];
    case 'rma':
      return [`What is the status of ${n}?`, `What was returned on ${n}?`];
    case 'fulfillment':
      return [`What shipped on ${n}?`, `Where is the tracking for ${n}?`];
    case 'quote':
      return [`Summarize quote ${n}`, `Has quote ${n} turned into an order?`];
    case 'item':
      return [`How many ${n} are in stock?`, `Which open orders need ${n}?`, `How is ${n} made?`];
    default:
      return [];
  }
}

/** The preview card's data (GET /flameco/record-search/preview). */
interface RecordPreview {
  type: string;
  number: string;
  status: string | null;
  stages: { labels: string[]; at: number } | null;
  facts: { label: string; value: string }[];
  netsuiteUrl: string;
}

/** How a NetSuite status reads at a glance. */
function statusTone(status: string | null | undefined): Row['tone'] {
  if (!status) return undefined;
  const s = status.toLowerCase();
  if (/closed|cancel|rejected|expired|voided/.test(s)) return 'hold';
  if (/billed|shipped|received|built|processed|fully|complete/.test(s) && !/partially|pending/.test(s)) return 'done';
  return 'open';
}

/** Reads like a question for Doug rather than a lookup. */
const QUESTION = /\?\s*$|^(how|what|why|when|where|who|which|can|could|does|do|is|are|should|show|explain|tell|list|summari[sz]e|find me)\b/i;
export const isQuestion = (q: string) => QUESTION.test(q.trim()) && q.trim().split(/\s+/).length >= 3;

/** Looks like a record number (WO24015, 26-2409, so 202609-913). */
const looksLikeNumber = (q: string) => /\d/.test(q) && !isQuestion(q);

/*
 * What this person searched for and opened, so the box can offer it again.
 * Kept in this browser per signed-in person (shop-floor tablets are shared).
 */
const RECENT_KEY = 'fl_search_recent_v2';
const USED_KEY = 'fl_search_used_v1';
const RECENT_MAX = 5;
const USED_MAX = 30;

/** The signed-in person's token claims (no network); null when unknown. */
function tokenClaims(config?: GlobalSearchConfig): Record<string, unknown> | null {
  try {
    const t = config?.getToken();
    const part = t?.split('.')[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/** The signed-in person's id from their token (no network); '' when unknown. */
export function searchWho(config?: GlobalSearchConfig): string {
  const c = tokenClaims(config);
  return c ? String(c.sub ?? c.employeeId ?? c.email ?? '') : '';
}

/**
 * Flameco AI in search is released to Administrators only for now: search
 * offers it only to them, on top of Alpha's own access check. (The backend
 * still enforces access; this keeps everyone else from seeing it at all.)
 */
const AI_ROLES = ['Administrator'];
export function searchAiAllowed(config?: GlobalSearchConfig): boolean {
  const role = tokenClaims(config)?.role;
  return typeof role === 'string' && AI_ROLES.includes(role);
}
function readJson<T>(key: string, fallback: T): T {
  try {
    return (JSON.parse(localStorage.getItem(key) || 'null') as T) ?? fallback;
  } catch {
    return fallback;
  }
}
function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode */
  }
}

function readRecent(who: string): string[] {
  const v = readJson<Record<string, string[]>>(RECENT_KEY, {})[who];
  return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [];
}
function saveRecent(who: string, q: string) {
  const query = q.trim();
  if (query.length < 2) return;
  const all = readJson<Record<string, string[]>>(RECENT_KEY, {});
  all[who] = [query, ...readRecent(who).filter((x) => x.toLowerCase() !== query.toLowerCase())].slice(0, RECENT_MAX);
  writeJson(RECENT_KEY, all);
}
function forgetRecent(who: string, q: string) {
  const all = readJson<Record<string, string[]>>(RECENT_KEY, {});
  all[who] = readRecent(who).filter((x) => x !== q);
  writeJson(RECENT_KEY, all);
}

/** A result this person opened; `url` is absolute so it works from any app. */
export interface UsedResult {
  kind: Kind;
  name: string;
  meta: string;
  url: string;
  external?: boolean;
  pill?: string;
  hue?: number | null;
  appKey?: FlamecoAppKey;
  count: number;
  at: number;
}
const USAGE_EVENT = 'fl-search-usage';
export function readUsed(who: string): UsedResult[] {
  const v = readJson<Record<string, UsedResult[]>>(USED_KEY, {})[who];
  return Array.isArray(v) ? v : [];
}
function saveUsed(who: string, r: Row) {
  if (!['app', 'page', 'record', 'person', 'doc', 'question'].includes(r.kind)) return;
  const url = r.local ? new URL(r.href, window.location.origin).href : r.href;
  const list = readUsed(who);
  const hit = list.find((x) => x.url === url);
  const now = Date.now();
  const next = hit
    ? list.map((x) => (x === hit ? { ...x, count: x.count + 1, at: now, name: r.name, meta: r.meta } : x))
    : [...list, { kind: r.kind, name: r.name, meta: r.meta, url, external: r.external, pill: r.pill, hue: r.hue, appKey: r.appKey, count: 1, at: now }];
  const all = readJson<Record<string, UsedResult[]>>(USED_KEY, {});
  // Most used first; older, rarely used ones fall off the end.
  all[who] = next.sort((a, b) => usedScore(b, now) - usedScore(a, now)).slice(0, USED_MAX);
  writeJson(USED_KEY, all);
  window.dispatchEvent(new Event(USAGE_EVENT));
}
/** Often and lately: each use counts less as weeks go by. */
const usedScore = (u: UsedResult, now: number) => u.count / (1 + (now - u.at) / (14 * 864e5));
/** This person's top results, most useful first. */
export function topUsed(who: string, n: number): UsedResult[] {
  const now = Date.now();
  return [...readUsed(who)].sort((a, b) => usedScore(b, now) - usedScore(a, now)).slice(0, n);
}

/**
 * How well a local row matches: every word must appear somewhere; the name
 * starting with the query beats a word in the name beats a word in the details.
 * -1 = no match.
 */
function score(r: Row, query: string, words: string[]): number {
  const name = r.name.toLowerCase();
  const hay = `${name} ${r.meta.toLowerCase()} ${(r.keywords ?? '').toLowerCase()}`;
  if (!words.every((w) => hay.includes(w))) return -1;
  let s = 0;
  if (name === query) s += 100;
  else if (name.startsWith(query)) s += 60;
  for (const w of words) {
    if (name.startsWith(w) || name.includes(` ${w}`)) s += 15;
    else if (name.includes(w)) s += 8;
  }
  if (r.local) s += 5;
  if (r.kind === 'app') s += 3;
  return s;
}

/** The typed words in bold inside a result's text. */
function Highlight({ text, words }: { text: string; words: string[] }) {
  if (!words.length) return <>{text}</>;
  const esc = words.filter((w) => w.length > 0).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!esc.length) return <>{text}</>;
  const parts = text.split(new RegExp(`(${esc.join('|')})`, 'ig'));
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1 ? <mark key={i} className="fl-smark">{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

function RowIcon({ r }: { r: Row }) {
  const cls = 'fl-icon';
  const icon =
    r.kind === 'app' && r.appKey && APP_ICONS[r.appKey] ? (
      APP_ICONS[r.appKey]({ className: cls })
    ) : r.kind === 'page' ? (
      <IconLayout className={cls} />
    ) : r.kind === 'record' || r.kind === 'loading' ? (
      <IconPackage className={cls} />
    ) : r.kind === 'person' ? (
      <IconUser className={cls} />
    ) : r.kind === 'question' ? (
      <IconQuestion className={cls} />
    ) : r.kind === 'doc' ? (
      <IconFile className={cls} />
    ) : r.kind === 'event' ? (
      <IconCalendar className={cls} />
    ) : r.kind === 'recent' ? (
      <IconHistory className={cls} />
    ) : (
      <IconSparkles className={cls} />
    );
  const hue = r.hue;
  return (
    <span
      className={`fl-plate fl-plate--sm fl-sicon${r.kind === 'doug' ? ' fl-sicon--doug' : ''}`}
      data-hue={hue == null ? undefined : ''}
      style={hue == null ? undefined : ({ '--fl-hue': hue } as React.CSSProperties)}
    >
      {icon}
    </span>
  );
}

export function useSearchShortcut(open: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        markSearchUsed();
        open();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
}

const TIP_KEY = 'fl_search_tip_v1';
/** How many page loads have shown the tip; it stops after three. */
const TIP_SHOWS_KEY = 'fl_search_tip_shows';
const TIP_MAX_SHOWS = 3;
/** The tip closes by itself after this long. */
const TIP_VISIBLE_MS = 2000;
const USED_EVENT = 'fl-search-used';

/** "Seen the Ctrl + K tip" lives on the person's Flameco account (the quick-access store, app "tips"). */
const TIP_ACCOUNT_APP = 'tips';
const TIP_ACCOUNT_KEY = 'search-shortcut';
let tipServer: GlobalSearchConfig | null = null;
let tipSaved = false;

const tipUrl = (c: GlobalSearchConfig) =>
  `${c.apiBase.replace(/\/+$/, '')}/flameco/quick-access?app=${TIP_ACCOUNT_APP}`;

/** Has this person (on any device, in any app) already found search? */
async function seenOnAccount(c: GlobalSearchConfig): Promise<boolean> {
  const token = c.getToken();
  if (!token) return false;
  try {
    const res = await fetch(tipUrl(c), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return false;
    const body = (await res.json()) as { favourites?: string[] } | null;
    return !!body?.favourites?.includes(TIP_ACCOUNT_KEY);
  } catch {
    return false;
  }
}

/**
 * The person has found search (opened it, or clicked Got it): the tip never
 * shows to them again, in any app or on any device.
 */
export function markSearchUsed() {
  try {
    localStorage.setItem(TIP_KEY, '1');
  } catch {
    /* private mode */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(USED_EVENT));
  const c = tipServer;
  const token = c?.getToken();
  if (!c || !token || tipSaved) return;
  tipSaved = true;
  fetch(tipUrl(c), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ favourites: [TIP_ACCOUNT_KEY], recent: [], updatedAt: Date.now() }),
  }).catch(() => {
    tipSaved = false;
  });
}

/** Mac shows ⌘, everyone else Ctrl. */
export function useShortcutKey(): string {
  const [key, setKey] = useState('Ctrl');
  useEffect(() => {
    const p = `${navigator.platform || ''} ${navigator.userAgent || ''}`;
    if (/Mac|iPhone|iPad/i.test(p)) setKey('⌘');
  }, []);
  return key;
}

/**
 * The "Search everything" box in the top bar: big enough to invite a click,
 * says what it finds, and shows the keyboard shortcut as real keys. Until the
 * person has used search (at most three page loads), a small tip under it
 * points out Ctrl + K for two seconds (desktop only; tablets have no keyboard).
 */
/** Has Alpha shown this person Doug on this page? (It decides a moment after load.) */
export function useDougReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let tries = 0;
    const check = () => {
      const ok = dougAvailable();
      setReady(ok);
      return ok;
    };
    if (check()) return;
    const t = setInterval(() => {
      if (check() || ++tries > 20) clearInterval(t);
    }, 1500);
    return () => clearInterval(t);
  }, []);
  return ready;
}

/** What the closed box suggests, one at a time: this person's own habits first, then examples. */
const HINTS = ['Search orders, people, pages and know-how', 'Try a work order or PO number', 'Try “on-time delivery” or “MTR”', 'Find a person or a how-to'];
const DOUG_HINT = 'Or ask a question: “How do I receive a PO?”';
const HINT_MS = 4500;

function usageHints(who: string): string[] {
  const out: string[] = [];
  for (const u of topUsed(who, 2)) out.push(`Jump back to ${u.pill ? `${u.pill} ` : ''}${u.name}`);
  const last = readRecent(who)[0];
  if (last && !out.some((h) => h.toLowerCase().includes(last.toLowerCase()))) out.push(`Search again: “${last}”`);
  return out;
}

/**
 * The closed box's text: examples one at a time (never with reduced motion),
 * plus whether to show the Doug mark. World's home uses it for its own box.
 */
export function useSearchHint(config?: GlobalSearchConfig): { hint: string; index: number; dougReady: boolean } {
  const dougReady = useDougReady() && searchAiAllowed(config);
  const [mine, setMine] = useState<string[]>([]);
  useEffect(() => {
    const load = () => setMine(usageHints(searchWho(config)));
    load();
    // The token may arrive a moment after the bar; and new habits after each search.
    const t = setTimeout(load, 2000);
    window.addEventListener(USAGE_EVENT, load);
    return () => {
      clearTimeout(t);
      window.removeEventListener(USAGE_EVENT, load);
    };
  }, [config]);
  const hints = useMemo(() => [HINTS[0], ...mine, ...HINTS.slice(1), ...(dougReady ? [DOUG_HINT] : [])], [mine, dougReady]);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') setIndex((h) => (h + 1) % hints.length);
    }, HINT_MS);
    return () => clearInterval(t);
  }, [hints.length]);
  return { hint: hints[index % hints.length], index, dougReady };
}

/** "Doug" mark inside a closed search box, for people who have Doug. */
export function SearchDougMark() {
  return (
    <span className="fl-search__doug" aria-hidden="true" title="Flameco AI answers what search can't">
      <IconSparkles className="fl-icon" />
      AI
    </span>
  );
}

export function SearchButton({
  onOpen,
  onVoice,
  config,
}: {
  onOpen: () => void;
  /** Mic in the bar: open search already listening. */
  onVoice?: () => void;
  config?: GlobalSearchConfig;
}) {
  const key = useShortcutKey();
  const [tip, setTip] = useState(false);
  const { hint, index, dougReady } = useSearchHint(config);
  const [canVoice, setCanVoice] = useState(false);
  useEffect(() => setCanVoice(!!onVoice && !!config && voiceSupported()), [onVoice, config]);
  useEffect(() => {
    let seen = true;
    let shows = 0;
    try {
      seen = localStorage.getItem(TIP_KEY) === '1';
      shows = Number(localStorage.getItem(TIP_SHOWS_KEY)) || 0;
    } catch {
      /* private mode: no tip */
    }
    const desktop = window.matchMedia?.('(pointer: fine) and (min-width: 901px)').matches;
    if (config) tipServer = config;
    if (seen || shows >= TIP_MAX_SHOWS || !desktop) return;
    let cancelled = false;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    const showTimer = setTimeout(async () => {
      // Already found search on another device or in another app: never again here either.
      if (config && (await seenOnAccount(config))) {
        try {
          localStorage.setItem(TIP_KEY, '1');
        } catch {
          /* private mode */
        }
        return;
      }
      if (cancelled) return;
      setTip(true);
      try {
        localStorage.setItem(TIP_SHOWS_KEY, String(shows + 1));
      } catch {
        /* private mode */
      }
      hideTimer = setTimeout(() => setTip(false), TIP_VISIBLE_MS);
    }, 1500);
    const hide = () => setTip(false);
    window.addEventListener(USED_EVENT, hide);
    return () => {
      cancelled = true;
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      window.removeEventListener(USED_EVENT, hide);
    };
  }, []);
  return (
    <div className="fl-search-wrap">
      <div className="fl-search fl-search-ring">
      <button
        type="button"
        className="fl-search__main"
        onClick={() => {
          markSearchUsed();
          onOpen();
        }}
        aria-label={`Search everything${dougReady ? ' or ask Flameco AI' : ''} (${key} K)`}
        aria-keyshortcuts="Control+K Meta+K"
      >
        <span className="fl-search__icon">
          <IconSearch className="fl-icon fl-icon--sm" />
        </span>
        <span className="fl-search__label" aria-hidden="true">
          <span key={index} className="fl-search__hint">
            {hint}
          </span>
        </span>
        <span className="fl-search__short" aria-hidden="true">
          Search
        </span>
        {dougReady && <SearchDougMark />}
        <span className="fl-search__keys" aria-hidden="true">
          <kbd className="fl-key">{key}</kbd>
          <kbd className="fl-key">K</kbd>
        </span>
      </button>
      {canVoice && (
        <button
          type="button"
          className="fl-search__mic"
          aria-label="Search by voice"
          title="Search by voice"
          onClick={() => {
            markSearchUsed();
            onVoice?.();
          }}
        >
          <IconMic className="fl-icon" />
        </button>
      )}
      </div>
      {tip && (
        <div className="fl-search-tip" role="status">
          <span>
            Tip: press <kbd className="fl-key">{key}</kbd> + <kbd className="fl-key">K</kbd> on any page to search people, apps and
            know-how.
          </span>
          <button type="button" className="fl-search-tip__ok" onClick={markSearchUsed}>
            Got it
          </button>
        </div>
      )}
    </div>
  );
}



/** What to try when the box is empty: clicking one types it. */
const EXAMPLES: { label: string; text: string; doug?: boolean }[] = [
  { label: 'A work order or PO number', text: 'WO ' },
  { label: 'On-time delivery', text: 'on-time delivery' },
  { label: 'MTR search', text: 'mtr' },
  { label: 'How do I …?', text: 'How do I ', doug: true },
];

export function GlobalSearch({
  open,
  onClose,
  apps,
  current,
  config,
  navigate,
  onAskDoug,
  listen,
  holdToTalk,
  initialQuery,
}: {
  open: boolean;
  onClose: () => void;
  apps: FlamecoApp[];
  current: FlamecoAppKey;
  config?: GlobalSearchConfig;
  /** In-app navigation for this app's own pages (router push). Default: full page load. */
  navigate?: (href: string) => void;
  /**
   * Hands a question to Doug. Default: the page's Doug widget (<alpha-chat>),
   * offered only while Alpha shows it to this person. Alpha itself passes its
   * own chat page here.
   */
  onAskDoug?: (q: string) => void;
  /** Opened from the mic: start listening straight away. */
  listen?: boolean;
  /**
   * With `listen`: opened by holding Space. Records until Space is let go
   * (pauses don't end it), then asks Flameco AI the words.
   */
  holdToTalk?: boolean;
  /** Text already typed (World's home box): the search opens with it. */
  initialQuery?: string;
}) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [scope, setScope] = useState<Scope>('all');
  const [world, setWorld] = useState<Row[]>([]);
  const [records, setRecords] = useState<Row[]>([]);
  const [loading, setLoading] = useState({ world: false, records: false });
  const [failed, setFailed] = useState({ world: false, records: false });
  const [recent, setRecent] = useState<string[]>([]);
  const [used, setUsed] = useState<UsedResult[]>([]);
  // Whose recent searches and shortcuts these are (shared tablets).
  const who = useMemo(() => (open ? searchWho(config) : ''), [open, config]);
  const [dougOk, setDougOk] = useState(false);
  const modKey = useShortcutKey();
  // "AI answer" card at the top of the results (Doug, answering right here).
  const doug = useDougAnswer();
  /** What the AI answer on show is about; null until one is asked for. */
  const [aiFor, setAiFor] = useState<string | null>(null);
  /** AI answers right here (not on Alpha's chat page or in the widget). */
  const [inlineAi, setInlineAi] = useState(false);
  const { ask: dougAsk, reset: dougReset } = doug;
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const byKey = useMemo(() => new Map(apps.map((a) => [a.key, a])), [apps]);
  const worldApp = byKey.get('world');
  const mfgApp = byKey.get('mfg');

  // Apps and every app's pages: instant, no network.
  const index = useMemo<Row[]>(() => {
    const rows: Row[] = apps.map((a) => ({
      kind: 'app',
      name: a.name === 'World' ? 'Flameco World' : a.name,
      meta: a.description + (a.onNetwork ? ' · on network' : ''),
      href: appUrl(a),
      hue: a.hue,
      appKey: a.key,
    }));
    for (const p of FLAMECO_PAGES) {
      const app = byKey.get(p.app);
      if (!app) continue;
      rows.push({
        kind: 'page',
        name: p.name,
        meta: p.app === current ? `${app.name === 'World' ? 'Flameco World' : app.name} · this app` : app.name === 'World' ? 'Flameco World' : app.name,
        keywords: p.keywords,
        href: p.app === current ? p.path : appUrl(app, p.path),
        local: p.app === current,
        hue: app.hue,
      });
    }
    return rows;
  }, [apps, byKey, current]);

  useEffect(() => {
    if (!open) return;
    setQ(initialQuery ?? '');
    setSel(0);
    setScope('all');
    setWorld([]);
    setRecords([]);
    setFailed({ world: false, records: false });
    setRecent(readRecent(who));
    setUsed(topUsed(who, 5));
    setAiFor(null);
    doug.reset();
    // Doug sits above everything and can keep the keyboard: out of the way while searching.
    minimizeDoug();
    hideDoug(true);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      hideDoug(false);
      doug.stop();
    };
    // doug's functions are stable; only opening matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Alpha decides who has Doug (it hides the widget for everyone else).
  useEffect(() => {
    if (!open) return;
    const ai = searchAiAllowed(config);
    setDougOk(ai && (!!onAskDoug || dougAvailable()));
    setInlineAi(ai && !onAskDoug && dougAvailable() && !!dougConnection());
  }, [open, q, onAskDoug, config]);

  const api = config ? config.apiBase.replace(/\/+$/, '') : '';
  const fetchJson = useCallback(
    async (path: string, signal: AbortSignal) => {
      const token = config?.getToken();
      const res = await fetch(`${api}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal });
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    },
    [api, config],
  );

  // People, knowledge and events from World, as the person types.
  useEffect(() => {
    const query = q.trim();
    if (!open || !config || !worldApp || query.length < 2) {
      setWorld([]);
      setLoading((l) => ({ ...l, world: false }));
      return;
    }
    const ctrl = new AbortController();
    // The last query's people are wrong for this one: drop them while this one loads.
    setWorld([]);
    setLoading((l) => ({ ...l, world: true }));
    const onWorld = current === 'world';
    const worldHref = (path: string) => (onWorld ? path : appUrl(worldApp, path));
    const t = setTimeout(async () => {
      try {
        const b = await fetchJson(`/flameco-world/search?q=${encodeURIComponent(query)}`, ctrl.signal);
        setWorld([
          ...(b.people ?? []).map((p: any) => ({
            kind: 'person' as const,
            name: p.name,
            meta: p.meta || 'Flameco World · people',
            href: worldHref(`/team?q=${encodeURIComponent(p.name)}${p.id ? `&person=${encodeURIComponent(p.id)}` : ''}`),
            local: onWorld,
            hue: 175,
          })),
          ...(b.posts ?? []).map((p: any) => ({
            kind: p.kind === 'question' ? ('question' as const) : ('doc' as const),
            name: p.name,
            meta: p.meta,
            href: worldHref(`/post?id=${encodeURIComponent(p.id)}`),
            local: onWorld,
            hue: 175,
          })),
          ...(b.events ?? []).map((e: any) => ({ kind: 'event' as const, name: e.name, meta: e.meta, href: worldHref('/calendar'), local: onWorld, hue: 45 })),
        ]);
        setFailed((f) => ({ ...f, world: false }));
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setWorld([]);
        setFailed((f) => ({ ...f, world: true }));
      } finally {
        if (!ctrl.signal.aborted) setLoading((l) => ({ ...l, world: false }));
      }
    }, 180);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [q, open, config, worldApp, current, fetchJson]);

  // Work orders, sales orders, POs, RMAs and items by number (NetSuite: a little slower, so a longer pause).
  useEffect(() => {
    const query = q.trim();
    if (!open || !config || query.length < 3 || isQuestion(query)) {
      setRecords([]);
      setLoading((l) => ({ ...l, records: false }));
      return;
    }
    const ctrl = new AbortController();
    setRecords([]);
    setLoading((l) => ({ ...l, records: true }));
    const onMfg = current === 'mfg';
    const t = setTimeout(async () => {
      try {
        const b = await fetchJson(`/flameco/record-search?q=${encodeURIComponent(query)}`, ctrl.signal);
        setRecords(
          (b.records ?? []).map((r: any): Row => {
            const meta =
              r.entity !== undefined
                ? [r.matchedPo ? `Customer PO ${r.matchedPo}` : null, r.entity, r.date].filter(Boolean).join(' · ') || r.meta
                : r.meta;
            const base = {
              kind: 'record' as const,
              pill: RECORD_PILL[r.kind] ?? 'Record',
              name: r.number,
              status: r.status ?? null,
              tone: statusTone(r.status),
              rec: r.id ? { kind: String(r.kind), id: String(r.id) } : undefined,
            };
            // Work orders and sales orders open in Manufacturing; the rest in NetSuite.
            const mfgPath =
              r.kind === 'workOrder'
                ? `/work-order-operations?tab=wo&wo=${encodeURIComponent(r.number)}`
                : r.kind === 'salesOrder'
                  ? `/work-order-operations?tab=so&so=${encodeURIComponent(r.number)}`
                  : null;
            if (mfgPath && mfgApp) {
              return { ...base, meta, href: onMfg ? mfgPath : appUrl(mfgApp, mfgPath), local: onMfg, netsuiteUrl: r.netsuiteUrl, hue: mfgApp.hue };
            }
            return { ...base, meta: `${meta ? `${meta} · ` : ''}NetSuite`, href: r.netsuiteUrl, external: true, hue: null };
          }),
        );
        setFailed((f) => ({ ...f, records: !!b.unavailable }));
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setRecords([]);
        setFailed((f) => ({ ...f, records: true }));
      } finally {
        if (!ctrl.signal.aborted) setLoading((l) => ({ ...l, records: false }));
      }
    }, 300);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [q, open, config, current, mfgApp, fetchJson]);

  const busy = loading.world || loading.records;
  const raw = q.trim();
  const words = useMemo(() => raw.toLowerCase().split(/\s+/).filter(Boolean), [raw]);

  /** Every match for the typed text, before the scope filter. */
  const matches = useMemo<Row[]>(() => {
    const query = raw.toLowerCase();
    if (!query) return [];
    const local = index
      .map((r) => ({ r, s: score(r, query, words) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 8)
      .map((x) => x.r);
    // NetSuite still looking: hold the orders' place so the list doesn't jump.
    const pending: Row[] =
      loading.records && !records.length && looksLikeNumber(raw)
        ? [{ kind: 'loading', name: 'Looking up NetSuite…', meta: '', href: '' }]
        : [];
    return [...records, ...pending, ...local, ...world];
  }, [raw, words, index, records, world, loading.records]);

  /**
   * The list as sections. With a query: Best match, where else it opens (a
   * work or sales order: Manufacturing or NetSuite), what to ask Flameco AI
   * about it, then everything else by group. A question puts Flameco AI first.
   */
  const sections = useMemo<{ heading: string; rows: Row[] }[]>(() => {
    if (!raw) {
      // What this person opens most, one tap away; then what they searched for.
      const out: { heading: string; rows: Row[] }[] = [];
      const shortcuts = used.map((u): Row => {
        const here = u.url.startsWith(`${window.location.origin}/`);
        const href = here ? u.url.slice(window.location.origin.length) : u.url;
        return { kind: u.kind, name: u.name, meta: u.meta, href, local: here, external: u.external, pill: u.pill, hue: u.hue, appKey: u.appKey };
      });
      if (shortcuts.length) out.push({ heading: 'Your shortcuts', rows: shortcuts });
      if (recent.length) out.push({ heading: 'Recent searches', rows: recent.map((r): Row => ({ kind: 'recent', name: r, meta: '', href: '' })) });
      const apps = index.filter((r) => r.kind === 'app' && !used.some((u) => u.kind === 'app' && u.name === r.name)).slice(0, 9);
      if (apps.length) out.push({ heading: 'Flameco apps', rows: apps });
      return out;
    }
    const groups = looksLikeNumber(raw) ? GROUPS : [...GROUPS.slice(1), GROUPS[0]];
    const ordered = groups.flatMap((g) => matches.filter((r) => g.kinds.includes(r.kind)));
    const question = isQuestion(raw);
    const best = question ? undefined : ordered.find((r) => r.kind !== 'loading');
    const out: { heading: string; rows: Row[] }[] = [];
    const asks: Row[] =
      dougOk && raw.length >= 3
        ? (best?.kind === 'record' ? dougQuestions(best) : [raw]).map((name): Row => ({ kind: 'doug', name, meta: '', href: '' }))
        : [];
    // Asked Flameco AI about exactly what's typed (Ctrl+Enter, hold-to-talk): that answer comes first.
    const askedNow = aiFor !== null && aiFor === raw;
    if (askedNow && !asks.some((a) => a.name === aiFor)) asks.unshift({ kind: 'doug', name: aiFor, meta: '', href: '' });
    if ((question || askedNow) && asks.length) out.push({ heading: 'Ask Flameco AI', rows: asks });
    if (best) out.push({ heading: 'Best match', rows: [best] });
    if (best?.kind === 'record' && !best.external && best.netsuiteUrl) {
      const what = best.rec?.kind === 'workOrder' ? 'work order' : 'sales order';
      out.push({
        heading: 'Open it in',
        rows: [
          { kind: 'open', name: 'Manufacturing App', meta: what, href: best.href, local: best.local, hue: mfgApp?.hue ?? null, appKey: 'mfg', rec: best.rec, keys: 'Shift' },
          { kind: 'open', name: 'NetSuite', meta: 'record page', href: best.netsuiteUrl, external: true, hue: null, rec: best.rec, keys: 'Alt' },
        ],
      });
    }
    if (!askedNow && best?.kind === 'record' && asks.length) out.push({ heading: 'Ask Flameco AI', rows: asks });
    for (const g of groups) {
      const rows = ordered.filter((r) => r !== best && g.kinds.includes(r.kind)).slice(0, 6);
      if (rows.length) out.push({ heading: best && g.kinds.includes(best.kind) ? `More ${g.label.toLowerCase()}` : g.label, rows });
    }
    if (!question && !askedNow && best?.kind !== 'record' && asks.length) out.push({ heading: best ? 'Not what you need?' : 'Ask Flameco AI', rows: asks });
    return out;
  }, [raw, matches, index, recent, used, dougOk, mfgApp, aiFor]);

  const counts = useMemo(() => {
    const c: Record<Scope, number> = { all: 0, records: 0, pages: 0, people: 0, knowledge: 0, doug: 0 };
    for (const sec of sections)
      for (const r of sec.rows) {
        if (r.kind === 'loading' || r.kind === 'recent') continue;
        c.all++;
        for (const s of SCOPES) if (s.kinds.includes(r.kind)) c[s.key]++;
      }
    return c;
  }, [sections]);

  /** Rows in display order, each with the heading it starts (if any). */
  const results = useMemo<{ row: Row; heading?: string }[]>(() => {
    if (!raw || scope === 'all') return sections.flatMap((sec) => sec.rows.map((row, i) => ({ row, heading: i === 0 ? sec.heading : undefined })));
    const allowed = SCOPES.find((s) => s.key === scope)!.kinds;
    return sections
      .flatMap((sec) => sec.rows)
      .filter((r) => allowed.includes(r.kind) || (scope === 'records' && r.kind === 'loading'))
      .map((row) => ({ row }));
  }, [raw, scope, sections]);

  /** Get an AI answer. auto: started by itself for a question (not saved as a recent search). */
  const ask = useCallback(
    (text: string, auto = false) => {
      const t = text.trim();
      if (!t) return;
      if (!auto) saveRecent(who, raw || t);
      // Alpha passes its own chat page; elsewhere the answer shows right here, with the widget as a fallback.
      if (onAskDoug) {
        onClose();
        onAskDoug(t);
      } else if (dougConnection()) {
        dougReset();
        setAiFor(t);
        void dougAsk(t);
      } else {
        onClose();
        askDoug(t);
      }
    },
    [who, raw, onClose, onAskDoug, dougAsk, dougReset],
  );

  // A new search: the last answer was about something else.
  useEffect(() => {
    dougReset();
    setAiFor(null);
  }, [raw, dougReset]);

  // Like a search engine's AI overview: a question, or a search that found
  // nothing, gets an answer by itself once the person stops typing.
  const found = counts.all - counts.doug > 0;
  useEffect(() => {
    if (!open || !inlineAi || raw.length < 3 || aiFor !== null) return;
    const question = isQuestion(raw);
    if (!question && (busy || found)) return;
    const t = setTimeout(() => ask(raw, true), question ? 900 : 500);
    return () => clearTimeout(t);
  }, [open, inlineAi, raw, busy, found, aiFor, ask]);

  /** A link in the AI's answer: this app's path in the router, a Flameco address by full load. */
  const openDougLink = useCallback(
    (href: string) => {
      if (!href) return;
      onClose();
      if (href.startsWith('/') && navigate) navigate(href);
      else window.location.assign(href);
    },
    [onClose, navigate],
  );

  // Voice: the words land in the box (a spoken question then gets its AI answer by itself).
  // Hold-to-talk: the words go straight to Flameco AI once the new search has settled.
  const talkAskRef = useRef<string | null>(null);
  const onVoiceText = useCallback(
    (text: string) => {
      setQ(text);
      setSel(0);
      inputRef.current?.focus();
      if (holdToTalk) talkAskRef.current = text;
    },
    [holdToTalk],
  );
  const voice = useVoiceSearch(config, onVoiceText);
  const { start: voiceStart, stop: voiceStop, cancel: voiceCancel } = voice;
  /** Space is still held down from opening the search by holding it. */
  const holdingRef = useRef(false);
  useEffect(() => {
    if (!open) {
      voiceCancel();
      holdingRef.current = false;
      return;
    }
    if (!listen) return;
    if (holdToTalk) {
      holdingRef.current = true;
      void voiceStart({ untilStop: true });
      return;
    }
    const t = setTimeout(() => void voiceStart(), 150);
    return () => clearTimeout(t);
  }, [open, listen, holdToTalk, voiceStart, voiceCancel]);
  // Letting go of Space ends the recording; while it is held it types nothing.
  useEffect(() => {
    if (!open || !holdToTalk) return;
    const down = (e: KeyboardEvent) => {
      if (holdingRef.current && e.code === 'Space') e.preventDefault();
    };
    const up = (e: KeyboardEvent) => {
      if (!holdingRef.current || e.code !== 'Space') return;
      e.preventDefault();
      holdingRef.current = false;
      voiceStop();
    };
    window.addEventListener('keydown', down, true);
    window.addEventListener('keyup', up, true);
    return () => {
      window.removeEventListener('keydown', down, true);
      window.removeEventListener('keyup', up, true);
    };
  }, [open, holdToTalk, voiceStop]);
  const listening = voice.state === 'listening';
  // Runs after the "new search" reset above, so the answer it starts isn't cleared.
  useEffect(() => {
    const t = talkAskRef.current;
    if (!t || t !== raw) return;
    talkAskRef.current = null;
    if (!dougOk) return;
    setSel(0);
    ask(t);
  }, [raw, dougOk, ask]);

  const openRow = useCallback(
    (r: Row) => {
      saveRecent(who, q);
      // "Open it in" rows are remembered as the record they open.
      if (r.kind === 'open') {
        const rec = results.find((x) => x.row.kind === 'record' && x.row.rec?.id === r.rec?.id)?.row;
        if (rec) saveUsed(who, { ...rec, href: r.href, local: r.local, external: r.external });
      } else saveUsed(who, r);
      onClose();
      if (r.external) window.open(r.href, '_blank', 'noopener');
      else if (r.local && navigate) navigate(r.href);
      else window.location.assign(r.href);
    },
    [who, q, results, onClose, navigate],
  );

  const choose = useCallback(
    (i: number) => {
      const r = results[i]?.row;
      if (!r || r.kind === 'loading') return;
      if (r.kind === 'recent') {
        setQ(r.name);
        setSel(0);
        inputRef.current?.focus();
        return;
      }
      if (r.kind === 'doug') return ask(r.name);
      openRow(r);
    },
    [results, ask, openRow],
  );

  /** Ask Flameco AI the first suggestion (Tab, or the Flameco AI chip). */
  const askFirst = useCallback(() => {
    const i = results.findIndex((x) => x.row.kind === 'doug');
    if (i >= 0) {
      setSel(i);
      ask(results[i].row.name);
    } else if (raw.length >= 3) ask(raw);
  }, [results, raw, ask]);

  const cycleScope = useCallback(
    (dir: 1 | -1) => {
      const visible = SCOPES.filter((s) => s.key === 'all' || counts[s.key] > 0);
      const i = visible.findIndex((s) => s.key === scope);
      setScope(visible[(i + dir + visible.length) % visible.length].key);
      setSel(0);
    },
    [counts, scope],
  );

  /** Moving through the list (↑↓) frees ←→ to switch the filter; typing gives them back to the text. */
  const navRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Enter' && listening) {
        // Enter while listening: done talking.
        e.preventDefault();
        voice.stop();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        navRef.current = true;
        setSel((s) => Math.min(s + 1, Math.max(results.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        navRef.current = true;
        setSel((s) => Math.max(s - 1, 0));
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && raw && navRef.current) {
        e.preventDefault();
        cycleScope(e.key === 'ArrowRight' ? 1 : -1);
      } else if (e.key === 'Enter' && (e.shiftKey || e.altKey)) {
        // Shift / Alt + Enter: the "Open it in" targets.
        const target = results.find((x) => x.row.kind === 'open' && x.row.keys === (e.shiftKey ? 'Shift' : 'Alt'));
        if (target) {
          e.preventDefault();
          openRow(target.row);
        }
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && dougOk) {
        // Ctrl+Enter: an AI answer, whatever is selected.
        e.preventDefault();
        ask(raw);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // Nothing to open: Enter gets the AI answer instead.
        if (!results.length && inlineAi && raw.length >= 3) ask(raw);
        else choose(sel);
      } else if (e.key === 'Tab' && raw) {
        e.preventDefault();
        if (dougOk && !e.shiftKey) askFirst();
        else cycleScope(e.shiftKey ? -1 : 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, listening, voice.stop, results, sel, choose, openRow, ask, askFirst, raw, dougOk, inlineAi, cycleScope, onClose]);

  // Results arriving can shorten the list under the cursor.
  useEffect(() => {
    setSel((s) => Math.min(s, Math.max(results.length - 1, 0)));
  }, [results.length]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-i="${sel}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  /* ---- the preview beside the list (wide screens) ---- */
  const selRow = results[sel]?.row;
  const previews = useRef(new Map<string, RecordPreview | 'none'>());
  const [, setPreviewTick] = useState(0);
  useEffect(() => {
    if (open) previews.current.clear();
  }, [open]);
  const recKey = selRow?.rec ? `${selRow.rec.kind}:${selRow.rec.id}` : '';
  useEffect(() => {
    if (!open || !config || !recKey || previews.current.has(recKey)) return;
    const [kind, id] = recKey.split(':');
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const b = await fetchJson(`/flameco/record-search/preview?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`, ctrl.signal);
        if (b?.preview) previews.current.set(recKey, b.preview);
        else previews.current.set(recKey, 'none');
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        previews.current.set(recKey, 'none');
      }
      if (!ctrl.signal.aborted) setPreviewTick((x) => x + 1);
    }, 120);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [open, config, recKey, fetchJson]);
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [sel, raw]);
  const copyLink = useCallback((r: Row) => {
    const url = r.local ? new URL(r.href, window.location.origin).href : r.href;
    void navigator.clipboard?.writeText(url).then(() => setCopied(true));
  }, []);

  if (!open) return null;
  const down = [failed.records && 'order and item numbers', failed.world && 'people and knowledge'].filter(Boolean).join(' and ');
  const showScopes = !!raw && counts.all > 0;
  // Phones (no preview pane): the AI answer sits on top of the list.
  const showAi = inlineAi && !!aiFor && scope === 'all';
  const recPreview = recKey ? previews.current.get(recKey) : undefined;

  const aiCard = (question: string) => (
    <AiAnswerCard
      turn={aiFor === question ? doug.turn : null}
      query={question}
      onAsk={() => ask(question)}
      onStop={doug.stop}
      onRate={doug.rate}
      onContinue={(t) => {
        onClose();
        doug.continueInChat(t);
      }}
      onLink={openDougLink}
      askKeys={modKey}
    />
  );

  const previewPane = (() => {
    const r = selRow;
    if (!r) {
      return (
        <div className="fl-spv fl-spv--empty">
          <IconSearch className="fl-icon" />
          <p>{busy ? 'Searching…' : 'Type an order number, a name, a page or a question.'}</p>
        </div>
      );
    }
    if (r.kind === 'doug') return <div className="fl-spv fl-spv--ai">{aiCard(r.name)}</div>;
    if (r.kind === 'loading') {
      return (
        <div className="fl-spv">
          <div className="fl-spv__skel">
            <i />
            <i />
            <i />
          </div>
        </div>
      );
    }
    if (r.kind === 'record' || r.kind === 'open') {
      const p = recPreview && recPreview !== 'none' ? recPreview : null;
      const loadingPv = !!r.rec && recPreview === undefined;
      const rec = r.kind === 'open' ? results.find((x) => x.row.kind === 'record' && x.row.rec?.id === r.rec?.id)?.row ?? r : r;
      const status = p?.status ?? rec.status ?? null;
      return (
        <div className="fl-spv">
          <div className="fl-spv__head">
            <div className="fl-spv__titles">
              <span className="fl-spv__eyebrow">{p?.type ?? rec.pill ?? 'Record'}</span>
              <span className="fl-spv__title">{p?.number ?? rec.name}</span>
            </div>
            {status && (
              <span className="fl-spv__status" data-tone={statusTone(status)}>
                <i />
                {status}
              </span>
            )}
          </div>
          {p?.stages && (
            <div className="fl-spv__stages" style={{ '--fl-n': p.stages.labels.length } as React.CSSProperties}>
              {p.stages.labels.map((l, i) => (
                <div key={l} className="fl-spv__stage" data-state={i < p.stages!.at ? 'done' : i === p.stages!.at ? 'now' : 'next'}>
                  <span className="fl-spv__bar" />
                  <span className="fl-spv__slabel">{l}</span>
                </div>
              ))}
            </div>
          )}
          {loadingPv ? (
            <div className="fl-spv__skel">
              <i />
              <i />
              <i />
            </div>
          ) : p ? (
            <dl className="fl-spv__facts">
              {p.facts.map((f) => (
                <div key={f.label} className="fl-spv__fact">
                  <dt>{f.label}</dt>
                  <dd title={f.value}>{f.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            rec.meta && <p className="fl-spv__meta">{rec.meta}</p>
          )}
          <div className="fl-spv__acts">
            <button type="button" className="fl-spv__btn fl-spv__btn--primary" onClick={() => openRow(r)}>
              {r.kind === 'open' ? `Open in ${r.name}` : 'Open record'}
              <kbd>↵</kbd>
            </button>
            <button type="button" className="fl-spv__btn" onClick={() => copyLink(r)}>
              <IconLink className="fl-icon" />
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>
      );
    }
    if (r.kind === 'recent') {
      return (
        <div className="fl-spv">
          <span className="fl-spv__eyebrow">Recent search</span>
          <span className="fl-spv__title fl-spv__title--sm">{r.name}</span>
          <div className="fl-spv__acts">
            <button type="button" className="fl-spv__btn fl-spv__btn--primary" onClick={() => choose(sel)}>
              Search again <kbd>↵</kbd>
            </button>
          </div>
        </div>
      );
    }
    const eyebrow: Record<string, string> = { app: 'Flameco app', page: 'Page', person: 'Person', doc: 'Knowledge', question: 'Question', event: 'Event' };
    return (
      <div className="fl-spv">
        <div className="fl-spv__hero">
          <RowIcon r={r} />
          <div className="fl-spv__titles">
            <span className="fl-spv__eyebrow">{eyebrow[r.kind] ?? ''}</span>
            <span className="fl-spv__title fl-spv__title--sm">{r.name}</span>
          </div>
        </div>
        {r.meta && <p className="fl-spv__meta">{r.meta}</p>}
        <div className="fl-spv__acts">
          <button type="button" className="fl-spv__btn fl-spv__btn--primary" onClick={() => openRow(r)}>
            Open{r.local ? '' : ' ↗'} <kbd>↵</kbd>
          </button>
          <button type="button" className="fl-spv__btn" onClick={() => copyLink(r)}>
            <IconLink className="fl-icon" />
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      </div>
    );
  })();

  return (
    <div className="fl-sov" onClick={onClose} role="presentation">
      <div className="fl-sbox fl-sbox--split" role="dialog" aria-modal="true" aria-label="Search everything" onClick={(e) => e.stopPropagation()}>
        <div className="fl-sinput">
          <IconSearch />
          <span className="fl-sfield">
          <input
            id="fl-global-search"
            ref={inputRef}
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
              navRef.current = false;
            }}
            placeholder={
              listening
                ? holdToTalk
                  ? 'Listening… talk to Flameco AI, then let go of Space'
                  : 'Listening… say a number, a page or a question'
                : voice.state === 'working'
                  ? 'Writing down what you said…'
                  : dougOk
                    ? ''
                    : 'Search orders, items, pages, people, know-how…'
            }
            aria-label={dougOk ? 'Search, or ask Flameco AI' : undefined}
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="search"
            role="combobox"
            aria-expanded="true"
            aria-controls="fl-global-search-list"
            aria-activedescendant={results.length ? `fl-srow-${sel}` : undefined}
          />
          {/* "ask Flameco AI" in the flame colour: a drawn placeholder (a real one can only take one colour). */}
          {!q && dougOk && !listening && voice.state !== 'working' && (
            <span className="fl-sph" aria-hidden="true">
              Search, or <b>ask Flameco AI</b>…
            </span>
          )}
          </span>
          {q && (
            <button
              type="button"
              className="fl-sclear"
              aria-label="Clear"
              onClick={() => {
                setQ('');
                setSel(0);
                inputRef.current?.focus();
              }}
            >
              ×
            </button>
          )}
          {voice.supported && config && (
            <button
              type="button"
              className={`fl-smic${listening ? ' is-on' : ''}`}
              style={{ '--fl-level': voice.level } as React.CSSProperties}
              aria-label={listening ? 'Stop listening' : 'Search by voice'}
              aria-pressed={listening}
              title={listening ? 'Stop listening' : 'Search by voice'}
              disabled={voice.state === 'working'}
              onClick={() => {
                voice.toggle();
                inputRef.current?.focus();
              }}
            >
              {voice.state === 'working' ? <span className="fl-spinner" aria-hidden="true" /> : <IconMic className="fl-icon" />}
            </button>
          )}
          {busy ? <span className="fl-spinner" aria-label="Searching" /> : null}
          {dougOk && (
            <button
              type="button"
              className="fl-sdoug"
              title="Ask Flameco AI (Tab)"
              disabled={raw.length < 3}
              onClick={() => {
                askFirst();
                inputRef.current?.focus();
              }}
            >
              <i aria-hidden="true" />
              Flameco AI
            </button>
          )}
          <button type="button" className="fl-kbd fl-sesc" onClick={onClose} aria-label="Close search">
            Esc
          </button>
        </div>
        {showScopes && (
          <div className="fl-sscopes" role="tablist" aria-label="Show">
            {SCOPES.filter((s) => s.key === 'all' || counts[s.key] > 0).map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={scope === s.key}
                className="fl-sscope"
                onClick={() => {
                  setScope(s.key);
                  setSel(0);
                  inputRef.current?.focus();
                }}
              >
                {s.label}
                <span className="fl-sscope__n">{counts[s.key]}</span>
              </button>
            ))}
          </div>
        )}
        <div className="fl-sbody">
          <div className="fl-slist" id="fl-global-search-list" role="listbox" ref={listRef}>
            {showAi && <div className="fl-sai-top">{aiCard(aiFor!)}</div>}
            {!raw && (
              <div className="fl-stry">
                <span className="fl-stry__label">Try</span>
                {EXAMPLES.filter((x) => !x.doug || dougOk).map((x) => (
                  <button
                    key={x.label}
                    type="button"
                    className="fl-stry__chip"
                    onClick={() => {
                      setQ(x.text);
                      setSel(0);
                      inputRef.current?.focus();
                    }}
                  >
                    {x.label}
                  </button>
                ))}
              </div>
            )}
            {results.length === 0 ? (
              <div className="fl-sempty">
                {busy
                  ? 'Searching…'
                  : raw.length < 3
                    ? 'Keep typing: order numbers and people need 3 or more characters.'
                    : 'No matches. Try a work order, sales order or PO number, a name, or a page.'}
              </div>
            ) : (
              results.map(({ row: r, heading }, i) => (
                <React.Fragment key={`${r.kind}-${r.href}-${r.name}-${i}`}>
                  {heading && <div className="fl-sgroup">{heading}</div>}
                  <div
                    id={`fl-srow-${i}`}
                    className={`fl-srow fl-srow--${r.kind}${heading === 'Best match' ? ' fl-srow--best' : ''}`}
                    role="option"
                    tabIndex={-1}
                    data-i={i}
                    aria-selected={i === sel}
                    aria-disabled={r.kind === 'loading' || undefined}
                    onMouseEnter={() => setSel(i)}
                    onClick={() => choose(i)}
                  >
                    {r.kind === 'doug' ? (
                      <span className="fl-plate fl-plate--sm fl-sicon fl-sicon--doug" aria-hidden="true">
                        <i className="fl-sdot" />
                      </span>
                    ) : (
                      <RowIcon r={r} />
                    )}
                    <span className="fl-stext">
                      <span className="fl-sname">
                        {r.pill && <span className="fl-spill">{r.pill}</span>}
                        {r.kind === 'doug' ? r.name : <Highlight text={r.name} words={r.kind === 'recent' || r.kind === 'open' ? [] : words} />}
                        {r.kind === 'open' && r.meta && <span className="fl-sopen-meta"> · {r.meta}</span>}
                      </span>
                      {r.meta && r.kind !== 'open' && r.kind !== 'doug' && (
                        <span className="fl-smeta">
                          <Highlight text={r.meta} words={r.kind === 'record' ? words : []} />
                        </span>
                      )}
                    </span>
                    {r.status && r.kind === 'record' && (
                      <span className="fl-sstatus" data-tone={r.tone}>
                        {r.status}
                      </span>
                    )}
                    {r.kind === 'recent' && (
                      <button
                        type="button"
                        className="fl-sact fl-sact--quiet"
                        aria-label={`Forget ${r.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          forgetRecent(who, r.name);
                          setRecent(readRecent(who));
                        }}
                      >
                        ×
                      </button>
                    )}
                    {r.kind === 'open' && r.keys ? (
                      <span className="fl-skeys" aria-hidden="true">
                        <kbd>{r.keys}</kbd>
                        <kbd>↵</kbd>
                      </span>
                    ) : r.kind === 'doug' ? (
                      <IconArrowRight className="fl-icon fl-sarrow" />
                    ) : heading === 'Best match' ? (
                      <span className="fl-skeys" aria-hidden="true">
                        <kbd>↵</kbd>
                      </span>
                    ) : r.kind !== 'recent' && r.kind !== 'loading' && !r.local ? (
                      <span className="fl-sext" title={r.external ? 'Opens in a new tab' : 'Opens in another Flameco app'}>
                        {'↗'}
                      </span>
                    ) : null}
                  </div>
                </React.Fragment>
              ))
            )}
          </div>
          <div className="fl-spane" aria-live="polite">
            {previewPane}
          </div>
        </div>
        {voice.state === 'error' && voice.error && <div className="fl-snote" role="status">{voice.error}</div>}
        {down && <div className="fl-snote" role="status">Couldn’t reach {down} just now. Other results are still shown.</div>}
        <div className="fl-sfoot" aria-hidden="true">
          <span>
            <kbd>↑↓</kbd> move
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          {showScopes && (
            <span>
              <kbd>← →</kbd> switch scope
            </span>
          )}
          {dougOk && (
            <span>
              <kbd>Tab</kbd> ask Flameco AI
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
